import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault
} from "use-query-params";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import ModalPreview from "components/Common/ModalPreview";
import {
  COLOR_STATUS_POST,
  STATUS_POST_OPTION,
} from "helpers/constans";
import { formatDateStringToKorea, formatISOString, getPlatFormImage } from "helpers/format";
import CountUp from "react-countup";
import { deleteCompany, getCompanies as onGetCompanies, putCompany } from "../../store/thunks";
import Form from "./Form";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import noImage1 from "../../assets/images/no-company.png";
import DropdownAsyncField from "components/Common/DropdownAsyncField";
import { isArray } from "lodash";
import CopyWrapper from "components/Common/CopyWrapper";
import { useNavigate } from 'react-router-dom';
import { ROUTES } from "routes/allRoutes";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import { useRole } from "components/Hooks/UserHooks";
import { capitalizeFirstLetter } from "utils";

registerLocale("en", en);
registerLocale("ko", ko);

const CompanyManagement = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    keyword: withDefault(StringParam, ""),
    website_url: withDefault(StringParam, ""),
    address: withDefault(StringParam, ""),
    contact_phone: withDefault(StringParam, ""),
    contact_name: withDefault(StringParam, ""),
    contact_email: withDefault(StringParam, ""),
    is_active: withDefault(StringParam, ""),
    fields: withDefault(ArrayParam, []),
    created_from: withDefault(StringParam, ""),
    created_to: withDefault(StringParam, ""),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [addressSearch, setAddressSearch] = useState<string>(
    query?.address || ""
  );
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [isMultiField, setIsMultiField] = useState<boolean>(false);

  const STATUS_USER_OPTION_LANG =
    STATUS_POST_OPTION?.map((item: any) => ({
      value: item?.value,
      label: t(item?.label),
    })) || [];

  const [nameSearch, setNameSearch] = useState<string>(query?.keyword || "");
  const [websiteSearch, setWebsiteSearch] = useState<string>(
    query?.website_url || ""
  );
  const [contactNameSearch, setContactNameSearch] = useState<string>(
    query?.contactName || ""
  );
  const [contactEmailSearch, setContactEmailSearch] = useState<string>(
    query?.contactEmail || ""
  );
  const [contactPhoneSearch, setContactPhoneSearch] = useState<string>(
    query?.contactPhone || ""
  );
  const [statusSearch, setStatusSearch] = useState<any>(
    STATUS_POST_OPTION?.find(
      (item) => item?.value === parseInt(query?.is_active)
    )?.value ?? ""
  );
  const [fieldsSearch, setFieldsSearch] = useState<any>({
    label: t("All Filter"),
    value: "",
  });
  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.created_from
      ? moment(query?.created_from || "", "Y-MM-DD").toDate()
      : null,
    query?.created_to ? moment(query?.created_to || "", "Y-MM-DD").toDate() : null,
  ]);
  const [created_from, created_to] = dateSearch;

  const [linkImage, setLinkImage] = useState<string>("");

  const selectLayoutState = (state: any) => state.Company;
  const CompanyProperties = createSelector(selectLayoutState, (state) => ({
    companies: state?.companies,
    isCompanyLoading: state.isCompanyLoading,
    isCompanySuccess: state.isCompanySuccess,

    error: state.error,
  }));

  // Inside your component
  const { companies, isCompanyLoading } = useSelector(CompanyProperties);

  useEffect(() => {
    dispatch(onGetCompanies(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    const formatData = fieldsSearch?.length > 0 ? fieldsSearch?.map((item: any) => (item?.value)) : [];

    setQuery({
      ...query,
      keyword: nameSearch.trim() || "",
      website_url: websiteSearch.trim() || "",
      address: addressSearch.trim() || "",
      fields: formatData,
      contact_phone: contactPhoneSearch.trim() || "",
      contact_name: contactNameSearch.trim() || "",
      contact_email: contactEmailSearch.trim() || "",
      is_active: statusSearch,
      created_from: created_from ? formatISOString(created_from, "start") : "",
      created_to: created_to ? formatISOString(created_to, "end") : "",
      //sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: "",
        website_url: "",
        address: "",
        contact_phone: "",
        contact_name: "",
        contact_email: "",
        is_active: "",
        fields: [],
        created_from: "",
        created_to: "",
        sort_by: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setNameSearch((_prev) => "");
    setWebsiteSearch((_prev) => "");
    setAddressSearch((_prev) => "");
    setContactEmailSearch((_prev) => "");
    setContactNameSearch((_prev) => "");
    setContactPhoneSearch((_prev) => "");
    setStatusSearch("");
    setFieldsSearch(null);
    setDateSearch([null, null]);
  };

  // Begin::Update User
  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(onGetCompanies(query));
  };
  //End::Update User

  // Begin::Change status
  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    is_active: number | boolean;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await putCompany(item?.id, {
        is_active: item?.is_active,
      });

      if (response?.data) {
        dispatch(onGetCompanies(query));

        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Change status

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {companies?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },

      {
        Header: t("Status"),
        accessor: "is_active",
        thWidth: 100,
        thClass: "text-center  whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={cell?.value}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    is_active: value,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Logo"),
        accessor: "logo_url",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 100,
        Cell: (cell: any) => (
          <>
            <div className="w-100 text-center " style={{ minWidth: "80px" }}>
              <div
                style={{ width: "80px", borderRadius: "10px" }}
                className="m-auto cursor-pointer"
                onClick={() => setLinkImage((prev: any) => cell?.value)}
              >
                <img
                  src={cell?.value ?? noImage1}
                  style={{ borderRadius: "10px" }}
                  className="avatar-sm"
                  alt="thumbnail"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = noImage1;
                  }}
                />
              </div>
            </div>
          </>
        ),
      },
      {
        Header: t("Company Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const company = cell?.value;
          return company ? <div className="cursor-pointer">
            <CopyWrapper contentCopy={company}>
              {`${company}`}
            </CopyWrapper>
          </div> : "-";
        },
      },
      {
        Header: t("Account"),
        accessor: "business_account",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 220,
        Cell: (cell: any) => {
          if (!cell?.value) return "-"
          const { email, provider } = cell?.value;
          const lowPlatform = provider ? provider.toLowerCase() : "";
          const imgPlatform = getPlatFormImage(provider);

          return <div onClick={() => navigate(`/user-management?user_email=${email}&country=&page=1&limit=30`)} style={{ cursor: "pointer" }}>
            <div className="d-flex gap-2 align-items-center">
              <img src={imgPlatform} alt={provider} width={25} height={25} />
              <div>
                {t(
                  capitalizeFirstLetter(
                    lowPlatform === "default" ||
                      lowPlatform == "guest"
                      ? "Email"
                      : lowPlatform
                  )
                )}
              </div>
            </div>
            <div style={{ maxWidth: "210px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{email}</div>
          </div>
        },
      },
      {
        Header: t("Fields"),
        accessor: "fields",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 240,
        Cell: (cell: any) => {
          const fields = cell?.value || [];

          return fields?.length > 0 ? (
            <div className="d-flex gap-2 flex-wrap justify-content-center" style={{ lineHeight: "16px", minWidth: "200px" }}>
              {fields?.map((item: any, index: number) => {
                const nameField = item?.translations?.length > 0 ? item?.translations?.find((field: any) => field?.lang === i18n?.language)?.title : '';
                return <span
                  className={`rounded-pill badge bg-primary`}
                  key={index}
                >
                  {`${nameField}`}
                </span>
              })}
            </div>
          ) : "-";
        },
      },
      {
        Header: t("Website"),
        accessor: "website_url",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const url = cell?.value;
          return url ? <div className="cursor-pointer text-primary">
            <CopyWrapper contentCopy={url}>
              {`${url}`}
            </CopyWrapper>
          </div> : '-';
        },
      },
      {
        Header: t("Address"),
        accessor: "address.address",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 220,
        Cell: (cell: any) => {
          const address = cell?.value;
          return address ? <div className="cursor-pointer">
            <CopyWrapper contentCopy={address}>
              {`${address}`}
            </CopyWrapper>
          </div> : "-";
        },
      },

      {
        Header: t("Location"),
        accessor: "address",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const item = cell?.value;
          const lat = item?.lat;
          const lng = item?.long;

          return (lat || lng) ? (
            <div className="cursor-pointer" style={{ minWidth: "120px" }}>
              <a
                href={`https://www.google.com/maps/search/${lat},${lng}/@${lat},${lng},17z?entry=ttu`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item?.address || item?.short_address ? (
                  <>{item?.short_address ?? item?.address}</>
                ) : (
                  <span className="d-flex align-items-center gap-1">
                    <i className="ri-map-pin-line"></i> {t("Location")}
                  </span>
                )}
              </a>
            </div>
          ) : (
            <></>
          );
        },
      },

      {
        Header: t("Contact Phone"),
        accessor: "contact_phone",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const phone = cell?.value;
          return phone ? <div className="cursor-pointer">
            <CopyWrapper contentCopy={phone}>
              {`${phone}`}
            </CopyWrapper>
          </div> : "-";
        },
      },
      {
        Header: t("Contact Name"),
        accessor: "contact_name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const name = cell?.value;
          return name ? <div className="cursor-pointer">
            <CopyWrapper contentCopy={name}>
              {`${name}`}
            </CopyWrapper>
          </div> : "-";
        },
      },

      {
        Header: t("Contact Email"),
        accessor: "contact_email",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const email = cell?.value;
          return email ? <div className="cursor-pointer">
            <CopyWrapper contentCopy={email}>
              {`${email}`}
            </CopyWrapper>
          </div> : '-';
        },
      },
      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Action"),
        thClass: "text-center",
        thWidth: 110,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              <TooltipCustom
                title={t("Button Update Company")}
                id={`update-us-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    setIsOpen(true);
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom"></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete Company")}
                id={`detail-cp-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-danger edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    setItemToDelete(item);
                  }}
                >
                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, companies?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  const onCloseImageClick = () => {
    setLinkImage((_prev) => "");
  };

  const checkHasAllOption = (lstUsers: any) => {
    let allUser = null;
    if (lstUsers instanceof Array) {
      allUser = lstUsers?.find((e: any) => e?.value == "all")
    }

    return allUser;
  }

  const handleSelectedField = (e: any) => {
    const allOption = checkHasAllOption(e);

    if (allOption) {
      setFieldsSearch(allOption)
      setIsMultiField(false)
    } else {
      setIsMultiField(true)
      if (isArray(e)) {
        setFieldsSearch(e)
      } else {
        setFieldsSearch([e])
      }
    }
  }

  // Begin::Delete
  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await deleteCompany(id);
      if (response?.statusCode) {
        dispatch(onGetCompanies(query));
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Delete

  useEffect(() => {
    if (userPermissions === "COMPANY" && !isHavePermissionRole([ROLES_FOR_APP.USER_MANAGEMENT], userPermissions)) {
      navigate(ROUTES.APPLICATION_MANAGEMENT, { replace: true });
    }
  }, [userPermissions, navigate]);

  useEffect(() => {
    document.title = `${t("Company Management")} | ShowStar`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Company Management")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={companies?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                    <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success d-md-block"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Company")}
                      </button>
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Name")}`}
                          isShow={!!nameSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}...`}
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Website")}`}
                          isShow={!!websiteSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Website")}...`}
                            value={websiteSearch}
                            onChange={(e) => setWebsiteSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Address")}`}
                          isShow={!!addressSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Address")}...`}
                            value={addressSearch}
                            onChange={(e) => setAddressSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Contact Phone")}`}
                          isShow={!!contactPhoneSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Contact Phone")}...`}
                            value={contactPhoneSearch}
                            onChange={(e) =>
                              setContactPhoneSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Contact Name")}`}
                          isShow={!!contactNameSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Contact Name")}...`}
                            value={contactNameSearch}
                            onChange={(e) =>
                              setContactNameSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Contact Email")}`}
                          isShow={!!contactEmailSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Contact Email")}...`}
                            value={contactEmailSearch}
                            onChange={(e) =>
                              setContactEmailSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={STATUS_USER_OPTION_LANG || []}
                            placeholder={`${t("Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_STATUS_POST}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Fields")} isShow={true}>
                          <DropdownAsyncField
                            name="fields"
                            placeholder={`${t("Fields")}...`}
                            isShowIconCheck={true}
                            hideSelectedOptions={false}
                            isAllOption={true}
                            listCheckedOption={fieldsSearch}
                            onChangeSelect={(e: any) => {
                              if (e?.length === 0 || e?.value === 'all') {
                                setIsMultiField(false)
                                setFieldsSearch({
                                  label: t("All Filter"),
                                  value: "all",
                                })
                              } else {
                                handleSelectedField(e);
                              }
                            }}
                            isClearable={false}
                            isMulti={isMultiField}
                            key={`category-${i18n?.language}`}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-3 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!created_from || !!created_to}
                        >
                          <DatePickerCustom
                            startDate={created_from || null}
                            endDate={created_to || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={9}
                        lg={9}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-bartender-media"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={companies?.items?.length ? companies?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={companies?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(companies?.pagination?.total) /
                        Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isCompanyLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={isOpen} centered={true} size="xl" scrollable={true}>
          <ModalHeader toggle={onCloseClick}>
            <>
              {!!itemToEdit?.id
                ? t("Button Update Company")
                : t("Button Create Company")}
            </>{" "}
            <>
              {itemToEdit?.id && (" - " + (itemToEdit?.name || "") || "")}
            </>
          </ModalHeader>
          <ModalBody>
            <div style={{ minHeight: "calc(100dvh - 200px)" }} className="mt-1">
              <Form
                id={String(itemToEdit?.id || "")}
                triggerRefresh={triggerRefresh}
                onClose={onCloseClick}
              />
            </div>
          </ModalBody>
        </Modal>

        {itemToDelete && (
          <ModalConfirm
            header={t("Button Delete Company")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage}
          url={linkImage}
          onClose={() => onCloseImageClick()}
        />
      </div>
    </React.Fragment>
  );
};

export default CompanyManagement;
